import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { RedirectError } from 'src/shared/error';

import { getDecisionMakerConfigs } from 'src/app/pages/petitionDetails/shared/api';
import { differenceInDays } from 'src/app/shared/utils/dates';

import type { GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables } from './api.graphql';
import { GammaPetitionDetailsData } from './api.graphql';
import { getPetitionAdvocates } from './getPetitionAdvocates';
import type { AdvocatesForPetitionQuery } from './getPetitionAdvocates/petitionAdvocates.graphql.d.ts';
import { getPetitionStrengthScore } from './getPetitionStrengthScore';
import { getSupporterVoicesVideos } from './supporterVoicesVideos';
import type { PetitionDetailsPageData } from './types';

async function fetchPetitionDetails(utilityContext: UtilityContext, variables: GammaPetitionDetailsDataQueryVariables) {
	const {
		gql: { fetch },
	} = utilityContext;
	const { data } = await fetch<GammaPetitionDetailsDataQuery, GammaPetitionDetailsDataQueryVariables>({
		query: GammaPetitionDetailsData,
		variables,
		rejectOnError: true,
		// to separate query from other batched queries
		batched: false,
	});

	return data;
}

function normalizePetition(
	petition: NonNullable<GammaPetitionDetailsDataQuery['petition']>,
): PetitionDetailsPageData['petition'] {
	return {
		...petition,
		primaryDecisionMakerConnection: {
			totalCount: petition.primaryDecisionMakerConnection ? petition.primaryDecisionMakerConnection.totalCount : 0,
			nodes: petition.primaryDecisionMakerConnection
				? petition.primaryDecisionMakerConnection.edges.map((edge) => ({
						...edge.node,
						targetPetitionAssociationData: edge.associationData,
					}))
				: [],
		},
		dmsWithLegislativeBodiesConnection: {
			pageInfo: { hasNextPage: petition.dmsWithLegislativeBodiesConnection?.pageInfo.hasNextPage || false },
			totalCount: petition.dmsWithLegislativeBodiesConnection?.totalCount || 0,
			nodes: petition.dmsWithLegislativeBodiesConnection?.nodes || [],
		},
	};
}

// eslint-disable-next-line max-lines-per-function
export async function getPetitionDetailsPageData(
	utilityContext: UtilityContext,
	slugOrId: string,
	{
		locale,
		countryCode,
		loadBandits,
		hasShowLegislativeBodiesParam,
	}: {
		locale: string;
		countryCode: string;
		loadBandits: boolean;
		hasShowLegislativeBodiesParam: boolean;
	},
): Promise<PetitionDetailsPageData> {
	const { contactDmConfig, dmWithLegislativeBodiesConnectionEnabled, legislativeBodiesEnabled } =
		await getDecisionMakerConfigs(utilityContext);

	const numDmsToFetch = hasShowLegislativeBodiesParam || legislativeBodiesEnabled ? null : 5;

	const data = await fetchPetitionDetails(utilityContext, {
		slugOrId,
		locale,
		countryCode,
		loadBandits,
		commentsSortBy: 'SCORE',
		emailEnabled: contactDmConfig.emailEnabled,
		numDmsToFetch,
		lbConnectionEnabled: dmWithLegislativeBodiesConnectionEnabled,
	});

	if (!data.petition) throw new RedirectError({ url: '/?petition_not_found=true' });

	const supporterVoicesVideos = await getSupporterVoicesVideos(
		utilityContext,
		slugOrId,
		!!data.petition.videoSettingsMetadata.disableDisplay,
	);

	const petition = normalizePetition(data.petition);

	let petitionAdvocates: AdvocatesForPetitionQuery['getAdvocatesForPetition'] = [];

	if (petition.isCommunityPetition) {
		const { getAdvocatesForPetition } = await getPetitionAdvocates(utilityContext, {
			slugOrId: petition.id,
		});

		petitionAdvocates = getAdvocatesForPetition;
	}

	return {
		petition,
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		createdLessThanOneDayAgo: differenceInDays(new Date(), new Date(data.petition.createdAt)) < 1,
		score: getPetitionStrengthScore(data.petition),
		supporterVoicesVideos,
		emailDm: { emailSubjectLine: contactDmConfig.emailSubjectLine, emailBody: contactDmConfig.emailBody },
		mediaHitsPage: {
			isLastPage: !petition.mediaHitsConnection.pageInfo.hasNextPage,
			numberOfStarterMedia: petition.mediaHitsConnection.totalCount,
			mediaHits: petition.mediaHitsConnection.nodes,
		},
		featureConfigs: data.featureConfigs,
		petitionAdvocates,
	};
}
